import React, { useEffect, useMemo, useRef, useState } from "react";

import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import TuneIcon from "@mui/icons-material/Tune";
import { Box, ListItemIcon, ListItemText, MenuItem, Toolbar } from "@mui/material";
import {
  ColumnSettingsPopover,
  ColumnSettingsPopoverProps
} from "components/reports/ReportMeasuresTable/ColumnSettingsPopover";
import { ColumnSettings } from "components/reports/ReportMeasuresTable/ReportMeasuresTable";
import { benchmarkColumnsFreemium, benchmarkFilters } from "constants/benchmark";
import {
  MaterialReactTable,
  MaterialReactTableProps,
  type MRT_ColumnDef,
  type MRT_SortingState,
  useMaterialReactTable
} from "material-react-table";
import { useGetBenchmarkFilters, useGetBenchmarkTable } from "queries/query-selectors/benchmark-query-selectors";
import { useAccountSelector, useReportsActions, useReportsQueriesSelector } from "store";
import { ClientDealTableRow, CurrencyType, DealTableColumn, ReportFilters, ReportTableColumn } from "store/types";

import { TableCell } from "../reports/Measures-table/TableCell";
import { ParametersPicker } from "./Table-components/ParametersPicker";
import { StaticFilters } from "./Table-components/StaticFilters";
import { TableHeader } from "./Table-components/TableHeader";

interface Props {
  currency: CurrencyType;
}

const defaultFilters = { [benchmarkFilters.SUB_ASSET]: [], [benchmarkFilters.VINTAGE]: [] };

const mapFilterKeyToServer = (key: string): string => {
  const keyMapping: Record<string, string> = {
    [benchmarkFilters.SUB_ASSET]: "Sub Asset Class",
    [benchmarkFilters.VINTAGE]: "Vintage"
  };
  return keyMapping[key] || key;
};

export const Table: React.FC<Props> = ({ currency }) => {
  const { benchmarkTableColumns, benchmarkIndexName } = useReportsQueriesSelector();
  const { data: filterValues } = useGetBenchmarkFilters({});
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const isFreemiumMode = useAccountSelector()?.is_freemium || false;

  const { data, isLoading, isFetching, error } = useGetBenchmarkTable({
    benchmarkTableColumns,
    currency,
    groupBy: [[benchmarkFilters.SUB_ASSET], [benchmarkFilters.VINTAGE]],
    exchangeMethod: "toDate",
    benchmarkIndexName: benchmarkIndexName,
    ...Object.entries(filters).reduce(
      (acc, [key, values]) => {
        if (values.length) {
          return { filters: { ...acc.filters, [mapFilterKeyToServer(key)]: values } };
        }
        return acc;
      },
      { filters: {} }
    )
  });

  const { setQueriesAction } = useReportsActions();

  // If client faced with error (like 402 - payment required), just set default columns and request table data with allowed columns
  useEffect(() => {
    if (error && isFreemiumMode) {
      setQueriesAction({
        benchmarkTableColumns: benchmarkColumnsFreemium
      });
    }
  }, [error, isFreemiumMode]);

  const columnRefs = useRef(new Map());

  const getColumnRef = (colId: string) => {
    if (!columnRefs.current.has(colId)) {
      columnRefs.current.set(colId, React.createRef<HTMLElement>());
    }
    return columnRefs.current.get(colId);
  };

  const [columnSettings, setColumnSettings] = useState<ColumnSettings>({ open: false, isDuplicate: false });

  const handleColumnSettings: ColumnSettingsPopoverProps["onApply"] = (queries) => {
    if (columnSettings.column) {
      const { column, isDuplicate } = columnSettings;
      const newKey = isDuplicate ? column.key + "-dup" : column.key;
      const newColumn: ReportTableColumn = {
        ...column,
        dateRange: queries?.dateRange,
        currency: queries?.currency,
        key: newKey
      };
      const index = benchmarkTableColumns.findIndex((col) => col === column);

      setQueriesAction({
        benchmarkTableColumns: [
          ...benchmarkTableColumns.slice(0, isDuplicate ? index + 1 : index),
          newColumn,
          ...benchmarkTableColumns.slice(index + 1)
        ]
      });
    }
    setColumnSettings({ open: false, isDuplicate: false });
  };

  const handleDelete = (column: ReportTableColumn) => {
    const index = benchmarkTableColumns.findIndex((col) => col.key === column?.key);
    if (index > -1) {
      setQueriesAction({
        benchmarkTableColumns: [...benchmarkTableColumns.slice(0, index), ...benchmarkTableColumns.slice(index + 1)]
      });
    }
  };

  const tableProps: Partial<MaterialReactTableProps<ClientDealTableRow>> = {
    enablePagination: false,
    enableDensityToggle: false,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: { padding: "0px", maxHeight: "calc(100vh - 220px)", overflow: "auto" }
    },
    muiTableProps: {
      sx: {
        borderRadius: "12px",
        border: "1px solid #DBE8F5"
      }
    },
    muiTableHeadProps: {
      sx: {
        backgroundColor: "#F6F8FC !important",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        boxShadow: "none !important",
        "& > :last-child": {
          borderTopRightRadius: "12px"
        }
      }
    },
    muiTableBodyProps: {
      sx: {
        "& > tr:last-of-type": {
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          "& > td:first-of-type": {
            borderBottomLeftRadius: "12px"
          },
          "& > td:last-of-type": {
            borderBottomRightRadius: "12px"
          }
        }
      }
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        bgcolor: "white",
        boxShadow: 0,
        "& .MuiTableContainer-root .MuiTable-root  .MuiTableBody-root  .MuiTableRow-root .MuiTableCell-root ": {
          "&:first-child": {
            overflow: "visible !important"
          }
        }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        "&:first-of-type": {
          borderRight: "none",
          borderTop: "0.5px solid #DBE8F5",
          borderBottom: "0.5px solid #DBE8F5",
          borderLeft: "0.5px solid #DBE8F5"
        },
        "&:not(:first-of-type):not(:nth-of-type(2))": {
          borderLeft: "0.5px solid #DBE8F5",
          borderTop: "0.5px solid #DBE8F5",
          borderBottom: "0.5px solid #DBE8F5"
        },
        "&:nth-of-type(2)": {
          borderTop: "0.5px solid #DBE8F5",
          borderBottom: "0.5px solid #DBE8F5"
        }
      }
    },
    muiTableHeadCellProps: {
      sx: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#F6F8FC !important",
        "&:first-of-type": {
          borderTopLeftRadius: "12px"
        },
        "&:last-of-type": {
          borderTopRightRadius: "12px"
        }
      }
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "#F6F8FC !important",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        boxShadow: "none !important",
        "& > :last-child": {
          borderTopRightRadius: "12px"
        }
      }
    },
    muiTableBodyRowProps: { sx: { backgroundColor: "white" } },
    defaultColumn: {
      muiColumnActionsButtonProps: { sx: { opacity: 0 } },
      muiTableHeadCellProps: {
        sx: {
          " .Mui-TableHeadCell-Content": { flexGrow: 1 },
          " .MuiDivider-root": {
            borderWidth: 1,
            opacity: 0
          }
        }
      }
    },
    icons: {
      ArrowDownwardIcon: (props: { className: string }) => (
        <div className="measures-table__sort-icons">
          <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
          <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
        </div>
      ),
      DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
    },
    muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),
    muiExpandAllButtonProps: { color: "primary" },
    muiBottomToolbarProps: { sx: { backgroundColor: "background.paper" } }
  };

  const columnsBenchmark = useMemo<MRT_ColumnDef<ClientDealTableRow>[]>(() => {
    // const result: MRT_ColumnDef<ClientDealTableRow>[] = [];

    if (!data) return [];

    const {
      tableData: { columns }
    } = data;

    // tableColumns.forEach((clientCol, index) => {
    //   const col = result.find((col) => col.id === clientCol.aggregation);
    //   if (col) {
    //     col.columns?.push(getColumnProps(columns[index], clientCol, index));
    //   } else {
    //     result.push({
    //       header: columns[index].title,
    //       id: clientCol.aggregation || `NO_AGG_${index}_${new Date().getTime()}`,
    //       columns: [getColumnProps(columns[index], clientCol, index)]
    //     });
    //   }
    // });

    const columnsTable: MRT_ColumnDef<ClientDealTableRow>[] = benchmarkTableColumns.map<
      MRT_ColumnDef<ClientDealTableRow>
    >((col, index) => {
      const colId = col.key + "-" + index;
      const columnData: DealTableColumn = columns.find((column) => column.key === col.key) || {
        key: "",
        title: "",
        postfix: ""
      };
      return {
        header: "",
        Header: ({ column }) => (
          <TableHeader
            indexName={benchmarkIndexName?.name}
            isBenchmarkTable={true}
            column={column}
            onClick={(e) => {
              e.stopPropagation();
              setColumnSettings({
                open: true,
                column: col,
                isDuplicate: false,
                anchorEl: getColumnRef(colId).current
              });
            }}
          />
        ),
        id: colId,
        meta: { data: columnData, def: col },
        accessorKey: col.key,
        sortingFn: "basic",
        accessorFn: (row) => row.valueData[col.key!],
        muiTableHeadCellProps: { ...tableProps.defaultColumn?.muiTableHeadCellProps, ref: getColumnRef(colId) },
        Cell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[column.columnDef.accessorKey as string] || ""}
            postfix={
              row.original.postfixes?.[column.columnDef.accessorKey as string] ||
              columnData.postfix ||
              row.original.postfix
            }
          />
        ),
        AggregatedCell: ({ row, column }) => (
          <TableCell
            id={column.id}
            value={row.original.valueData[column.columnDef.accessorKey as string] || ""}
            postfix={
              row.original.postfixes?.[column.columnDef.accessorKey as string] ||
              columnData.postfix ||
              row.original.postfix
            }
            aggregated
          />
        )
      };
    });

    columnsTable.unshift({
      header: "Asset Class",
      id: "title",
      accessorKey: "title",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableSorting: false,
      enableColumnOrdering: false,
      muiTableHeadCellProps: {
        sx: {
          fontSize: "16px",
          color: "#0A3142",
          fontWeight: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }
      },
      Cell: ({ cell, row }) => (
        <TableCell
          indentation={row.getParentRow()?.original.deal_id ? 2 : 0}
          dealId={row.original.deal_id}
          value={cell.getValue() as string}
          formatDate
        />
      ),
      AggregatedCell: ({ cell, row }) => (
        <TableCell dealId={row.original.deal_id} value={cell.getValue() as string} aggregated formatDate />
      )
    });

    return columnsTable;
  }, [data, benchmarkIndexName, benchmarkTableColumns]);

  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const sortedRows = useMemo(() => {
    const rows = data?.tableData.rows || [];
    if (!rows.length) return rows;

    const [totalRow, ...rest] = rows;

    if (!sorting.length) return rows;

    const { id: sortColumnId, desc } = sorting[0];
    // Column id is "key-index" (e.g. "irr-0"), accessorKey is just "key" (e.g. "irr")
    const accessorKey = sortColumnId === "title" ? "title" : sortColumnId.replace(/-\d+$/, "");

    const sorted = [...rest].sort((a, b) => {
      const aVal = accessorKey === "title" ? a.title : (a.valueData[accessorKey] ?? "");
      const bVal = accessorKey === "title" ? b.title : (b.valueData[accessorKey] ?? "");
      if (aVal < bVal) return desc ? 1 : -1;
      if (aVal > bVal) return desc ? -1 : 1;
      return 0;
    });

    return [totalRow, ...sorted];
  }, [data, sorting]);

  const table = useMaterialReactTable({
    ...tableProps,
    columns: columnsBenchmark,
    data: sortedRows,
    manualSorting: true,
    onSortingChange: setSorting,
    state: {
      isLoading: !data || isLoading || isFetching,
      columnOrder: columnOrder,
      sorting
    },
    localization: {
      sortByColumnAsc: "Sort",
      sortByColumnDesc: "Sort",
      sortedByColumnAsc: "Increase",
      sortedByColumnDesc: "Decrease",
      clearSort: "Clear Sort"
    },
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableStickyHeader: true,
    enableExpanding: true,
    enableColumnDragging: true,
    enableColumnActions: true,
    enableColumnOrdering: true,
    onColumnOrderChange: setColumnOrder,
    enableBottomToolbar: false,
    layoutMode: "grid",
    enableColumnResizing: false,
    defaultColumn: {
      muiColumnDragHandleProps: {
        sx: { position: "absolute", left: -22, top: 0, opacity: 0.7, "&:hover": { opacity: 1 } }
      },
      enableColumnActions: true,
      renderColumnActionsMenuItems: ({ column, closeMenu, table }) => {
        const otherColumns = table.getAllColumns().filter((col) => col !== column);
        const deletable = otherColumns.some(
          //@ts-ignore
          (col) => col.columnDef.meta?.def?.aggregation === column.columnDef.meta?.def?.aggregation
        );
        const columnDef = benchmarkTableColumns.find((col) => col.key === column.columnDef.accessorKey);

        return [
          <MenuItem
            key="duplicate"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: columnDef!,
                isDuplicate: true,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Duplicate Column</ListItemText>
          </MenuItem>,
          <MenuItem
            key="settings"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: columnDef!,
                isDuplicate: false,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <TuneIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Column Settings</ListItemText>
          </MenuItem>,
          ...(deletable
            ? [
                <MenuItem
                  key="delete"
                  onClick={() => {
                    handleDelete(columnDef!);
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              ]
            : [])
        ];
      }
    },

    icons: {
      ArrowDownwardIcon: (props: { className: string }) => (
        <div className="measures-table__sort-icons">
          <ArrowDropUp className={"measures-table__arrow_up " + props.className} />
          <ArrowDropDown className={"measures-table__arrow_down " + props.className} />
        </div>
      ),
      DragHandleIcon: (props: any) => <DragIndicatorIcon color={"primary"} {...props} />
    },

    muiExpandButtonProps: ({ row }) => (row.getCanExpand() ? { color: "primary" } : { sx: { visibility: "hidden" } }),

    muiExpandAllButtonProps: { color: "primary" },

    renderTopToolbar: () => (
      <Toolbar
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          color: "#173c4c",
          fontSize: "16px",
          height: "24px",
          minHeight: "24px !important",
          maxHeight: "24px",
          px: "0 !important",
          mb: "16px"
        }}
      >
        <StaticFilters filters={filters} filterValues={filterValues || {}} setFilters={setFilters} />
        <ParametersPicker />
      </Toolbar>
    )
  });

  const popover = (
    <ColumnSettingsPopover
      {...columnSettings}
      onClose={() => setColumnSettings({ open: false, isDuplicate: false })}
      onApply={handleColumnSettings}
      isBenchmarkTable={true}
    />
  );

  return (
    <Box
      className="measures-table"
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: "white",
        p: "16px",
        boxShadow: "0 1px 2px 0 #1212171a",
        borderRadius: "8px",
        overflow: "hidden",
        ".MuiPaper-root": {
          bgcolor: "transparent",
          height: "100%",
          boxShadow: "none",
          "> .MuiBox-root": {
            display: "none"
          }
        }
      }}
    >
      <MaterialReactTable table={table} />
      {popover}
    </Box>
  );
};
